class CaminoSubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
    this.form = document.getElementById(this.dataset.productFormId);
    if (!this.form) return;

    this.currency = this.dataset.currency || 'EUR';
    this.locale = this.dataset.locale === 'en' ? 'en-GB' : 'nl-NL';
    this.threshold = Number(this.dataset.freeShippingThreshold || 0);
    this.variantIdInput = this.form.querySelector('input[name="id"]');
    this.planInput = this.ensurePlanInput();
    this.hiddenQuantityInput = this.form.querySelector('input[name="quantity"]');
    this.nativeQuantityInput = this.form.querySelector('input[name="quantity-js"]');
    this.nativeQuantityWrapper = this.form.querySelector('.product-form__quantity-wrapper');
    this.purchaseInputs = [...this.querySelectorAll('input[name^="camino-purchase-type"]')];
    this.planSelect = this.querySelector('[data-selling-plan-select]');
    this.usageSelect = this.querySelector('[data-cups-per-day]');
    this.quantityOutput = this.querySelector('[data-quantity-value]');
    this.subscriptionPrice = this.querySelector('[data-subscription-price]');
    this.oneTimePrice = this.querySelector('[data-one-time-price]');
    this.oneTimePriceDetail = this.querySelector('[data-one-time-price-detail]');
    this.savingSummary = this.querySelector('[data-saving-summary]');
    this.annualSaving = this.querySelector('[data-annual-saving]');
    this.shippingMessage = this.querySelector('[data-shipping-message]');
    this.subscriptionOnly = [...this.querySelectorAll('[data-subscription-only]')];
    this.calculatorResult = this.querySelector('[data-calculator-result]');
    this.cupsPerDay = Number(this.usageSelect?.value || 2);
    this.quantity = this.currentQuantity();
    // Consumption is optional guidance, not a hidden controller. Until the
    // customer uses the helper, bags and delivery cadence are independent.
    this.usageHelperEngaged = false;
    this.manualPlanOverride = false;

    this.populatePlans();
    this.applyDefaultCadence();
    this.bindEvents();
    this.startDefaultWidgetObserver();
    this.hideDefaultWidget();
    this.render();
  }

  disconnectedCallback() {
    this.defaultWidgetObserver?.disconnect();
  }

  ensurePlanInput() {
    const inputs = [...this.form.querySelectorAll('input[name="selling_plan"]')];
    let input = inputs.find((element) => element.dataset.caminoSellingPlan === 'true');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.dataset.caminoSellingPlan = 'true';
      this.form.appendChild(input);
    }
    inputs.filter((element) => element !== input).forEach((element) => element.disabled = true);
    return input;
  }

  startDefaultWidgetObserver() {
    if (this.dataset.hideDefaultWidget !== 'true') return;

    this.defaultWidgetObserver?.disconnect();
    this.defaultWidgetObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length > 0)) {
        this.hideDefaultWidget();
      }
    });
    this.defaultWidgetObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  hideDefaultWidget() {
    if (this.dataset.hideDefaultWidget !== 'true') return;
    const selector = this.dataset.defaultWidgetSelector;
    if (!selector) return;

    if (!document.getElementById('camino-hidden-appstle-widget-style')) {
      const style = document.createElement('style');
      style.id = 'camino-hidden-appstle-widget-style';
      style.textContent = '[data-camino-hidden-appstle-widget="true"] { display: none !important; }';
      document.head.appendChild(style);
    }

    document.querySelectorAll(selector).forEach((element) => {
      if (!this.contains(element)) {
        element.dataset.caminoHiddenAppstleWidget = 'true';
        element.setAttribute('aria-hidden', 'true');
      }
    });
  }

  currentQuantity() {
    const value = Number(this.nativeQuantityInput?.value || this.hiddenQuantityInput?.value || 1);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
  }

  selectedVariant() {
    const id = Number(this.variantIdInput?.value);
    return this.product.variants.find((variant) => Number(variant.id) === id) || this.product.variants[0];
  }

  plans() {
    return (this.product.selling_plan_groups || []).flatMap((group) => group.selling_plans || []);
  }

  selectedPlan() {
    return this.plans().find((plan) => String(plan.id) === String(this.planSelect.value)) || this.plans()[0];
  }

  isSubscription() {
    return this.purchaseInputs.find((input) => input.checked)?.value === 'subscription';
  }

  populatePlans() {
    this.planSelect.innerHTML = this.plans().map((plan) => `<option value="${plan.id}">${plan.name}</option>`).join('');
  }

  weeksForPlan(plan) {
    const match = (plan?.name || '').match(/(\d+)/);
    return match ? Number(match[1]) : 4;
  }

  computedPlan() {
    const desiredWeeks = (this.quantity * 50) / this.cupsPerDay / 7;
    return this.plans()
      .map((plan) => ({ plan, difference: Math.abs(this.weeksForPlan(plan) - desiredWeeks) }))
      .sort((a, b) => a.difference - b.difference)[0]?.plan;
  }

  defaultPlan() {
    return this.plans().find((plan) => this.weeksForPlan(plan) === 4) || this.plans()[0];
  }

  applyDefaultCadence() {
    const plan = this.defaultPlan();
    if (plan) this.planSelect.value = plan.id;
    this.manualPlanOverride = false;
  }

  applyComputedCadence() {
    const plan = this.computedPlan();
    if (plan) this.planSelect.value = plan.id;
    this.manualPlanOverride = false;
  }

  bindEvents() {
    this.purchaseInputs.forEach((input) => input.addEventListener('change', () => this.render()));
    this.planSelect.addEventListener('change', () => {
      this.manualPlanOverride = true;
      this.render();
    });
    this.usageSelect?.addEventListener('change', () => {
      this.cupsPerDay = Number(this.usageSelect.value);
      this.usageHelperEngaged = true;
      this.applyComputedCadence();
      this.render();
    });
    this.querySelector('[data-quantity-decrease]').addEventListener('click', () => this.setQuantity(this.quantity - 1));
    this.querySelector('[data-quantity-increase]').addEventListener('click', () => this.setQuantity(this.quantity + 1));
    if (typeof eventBus !== 'undefined' && eventBus.on) eventBus.on('variant:change', this.handleVariantChange);
    document.addEventListener('variant:change', this.handleVariantChange);
    this.variantIdInput?.addEventListener('change', this.handleVariantInputChange);
  }

  disconnectedCallback() {
    if (typeof eventBus !== 'undefined' && eventBus.off) eventBus.off('variant:change', this.handleVariantChange);
    document.removeEventListener('variant:change', this.handleVariantChange);
    this.variantIdInput?.removeEventListener('change', this.handleVariantInputChange);
  }

  handleVariantInputChange = () => this.render();

  handleVariantChange = (event) => {
    const variant = event?.variant || event?.detail?.variant;
    if (!variant || !this.variantIdInput) return;
    this.variantIdInput.value = variant.id;
    this.render();
  }

  subscriptionAllocation() {
    const variant = this.selectedVariant();
    const plan = this.selectedPlan();
    return (variant.selling_plan_allocations || []).find((allocation) => String(allocation.selling_plan_id) === String(plan.id)) || null;
  }

  money(cents) {
    return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(Number(cents || 0) / 100);
  }

  setQuantity(nextQuantity) {
    this.quantity = Math.max(1, Math.floor(nextQuantity || 1));
    this.syncQuantity();
    if (this.usageHelperEngaged) this.applyComputedCadence();
    this.render();
  }

  syncQuantity() {
    if (this.hiddenQuantityInput) this.hiddenQuantityInput.value = this.quantity;
    if (this.nativeQuantityInput) {
      this.nativeQuantityInput.value = this.quantity;
      this.nativeQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  render() {
    const variant = this.selectedVariant();
    const allocation = this.subscriptionAllocation();
    const oneTimeUnitPrice = Number(variant?.price || 0);
    const subscriptionUnitPrice = Number(allocation?.price || oneTimeUnitPrice);
    const oneTimeTotal = oneTimeUnitPrice * this.quantity;
    const subscriptionTotal = subscriptionUnitPrice * this.quantity;
    const isSubscription = this.isSubscription();
    const activeTotal = isSubscription ? subscriptionTotal : oneTimeTotal;
    const saving = Math.max(0, oneTimeTotal - subscriptionTotal);
    const activeUnitPrice = isSubscription ? subscriptionUnitPrice : oneTimeUnitPrice;
    const freeShipping = this.threshold > 0 && activeTotal >= this.threshold;
    const bagsToFreeShipping = this.threshold > 0 && activeUnitPrice > 0
      ? Math.max(0, Math.ceil((this.threshold - activeTotal) / activeUnitPrice))
      : 0;
    const freeShippingTarget = this.quantity + bagsToFreeShipping;
    const cadenceWeeks = this.weeksForPlan(this.selectedPlan());
    const annualSaving = saving * (52 / cadenceWeeks);
    const recommendedPlan = this.computedPlan();

    this.subscriptionPrice.textContent = this.money(subscriptionTotal);
    this.oneTimePrice.textContent = this.money(oneTimeTotal);
    this.oneTimePriceDetail.textContent = `${this.quantity} × ${this.money(oneTimeUnitPrice)}`;
    this.savingSummary.textContent = this.dataset.savingTemplate.replace('{delivery}', this.money(saving));
    this.annualSaving.textContent = this.dataset.annualSavingTemplate.replace('{year}', this.money(annualSaving));
    this.quantityOutput.textContent = this.quantity;
    this.planInput.value = isSubscription && allocation ? allocation.selling_plan_id : '';
    this.planInput.disabled = !isSubscription || !allocation;
    this.subscriptionOnly.forEach((element) => element.hidden = !isSubscription);
    this.classList.toggle('is-one-time', !isSubscription);
    if (this.nativeQuantityWrapper) {
      this.nativeQuantityWrapper.hidden = true;
      this.nativeQuantityWrapper.style.setProperty('display', 'none', 'important');
    }

    this.querySelectorAll('.camino-subscription-widget__option').forEach((option) => option.classList.toggle('is-selected', option.querySelector('input').checked));
    if (freeShipping) {
      this.shippingMessage.textContent = this.dataset.freeShippingLabel;
      this.shippingMessage.classList.add('is-free-shipping');
    } else {
      this.shippingMessage.textContent = this.dataset.shippingNudgeTemplate
        .replace('{target}', freeShippingTarget);
      this.shippingMessage.classList.remove('is-free-shipping');
    }
    this.calculatorResult.textContent = this.dataset.recommendationTemplate
      .replace('{cadence}', this.weeksForPlan(recommendedPlan));
  }
}

if (!customElements.get('camino-subscription-widget')) customElements.define('camino-subscription-widget', CaminoSubscriptionWidget);
